/**
 * ISSUE-1364. The words and rules of an officer's timeout on the web, after
 * v1's `timeout_sheet.dart`: four lengths, an optional reason, the composer
 * line the timed-out fan reads, and which rows offer Time out or End timeout.
 * Pure, so every rule is asserted as data.
 */

export type typeTimeoutDuration =
  "tenMinutes" | "oneHour" | "oneDay" | "oneWeek";

export const TIMEOUT_DURATIONS: readonly {
  value: typeTimeoutDuration;
  label: string;
}[] = [
  { value: "tenMinutes", label: "10 minutes" },
  { value: "oneHour", label: "1 hour" },
  { value: "oneDay", label: "1 day" },
  { value: "oneWeek", label: "1 week" },
];

export const DEFAULT_TIMEOUT_DURATION: typeTimeoutDuration = "oneHour";
export const TIMEOUT_REASON_MAX_LENGTH = 200;

export const TIMEOUT_ACTION_LABEL = "Time out";
export const END_TIMEOUT_ACTION_LABEL = "End timeout";
export const TIMEOUT_DURATION_LABEL = "How long";
export const TIMEOUT_REASON_LABEL = "Reason (optional)";
export const TIMEOUT_REASON_HINT = "Only they see this.";
export const TIMEOUT_DIALOG_BODY =
  "They can still read and react, but cannot post until it ends.";

export function timeoutDialogTitle(displayName: string): string {
  return `Time out ${displayName}`;
}

/**
 * `3:15 PM` today, `Sat, Nov 8, 3:15 PM` on any other day, on the device's
 * own clock, the way the mute line reads.
 */
export function formatTimeoutEnd(untilIso: string, now: Date): string {
  const until = new Date(untilIso);
  const isSameDay = until.toDateString() === now.toDateString();
  return new Intl.DateTimeFormat(
    undefined,
    isSameDay
      ? { hour: "numeric", minute: "2-digit" }
      : {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        },
  ).format(until);
}

/**
 * ISSUE-1368. What a fan reads where the composer would be when staff have
 * switched chat off for their whole account. Every chat, no end time, so it
 * names neither.
 */
export const CHAT_SUSPENDED_COMPOSER_LINE =
  "Our staff turned off posting for your account. You can still read chats and react.";

/** What the timed-out fan reads where the composer would be. */
export function timedOutComposerLine(input: {
  untilIso: string;
  reason: string | null;
  now: Date;
}): string {
  const base = `You're timed out until ${formatTimeoutEnd(input.untilIso, input.now)}.`;
  const reason = input.reason?.trim() ?? "";
  return reason === "" ? base : `${base} Reason: "${reason}"`;
}

/** The roster caption under a member an officer timed out. */
export function timedOutRosterLabel(untilIso: string, now: Date): string {
  return `Timed out until ${formatTimeoutEnd(untilIso, now)}`;
}

/** A running timeout, or null when there is none or it has passed. */
export function activeTimeoutIso(
  untilIso: string | null | undefined,
  now: Date,
): string | null {
  if (untilIso === null || untilIso === undefined || untilIso === "") {
    return null;
  }
  return new Date(untilIso).getTime() > now.getTime() ? untilIso : null;
}

/** The longest delay `setTimeout` takes before it fires at once instead. */
const MAX_TIMER_MS = 2_147_483_647;

/**
 * How long after `now` to re-read `/chat/get` so the composer comes back on
 * its own when a timeout runs out while the chat is open, or null when there
 * is no running timeout. A second past the end, so the server agrees it is
 * over.
 */
export function timeoutRefetchDelayMs(
  untilIso: string | null | undefined,
  now: Date,
): number | null {
  const active = activeTimeoutIso(untilIso, now);
  if (active === null) return null;
  return Math.min(
    new Date(active).getTime() - now.getTime() + 1000,
    MAX_TIMER_MS,
  );
}

/**
 * Which timeout action a roster row offers: none for yourself, for anyone
 * when you are not an officer, or for another officer; otherwise Time out,
 * or End timeout while one is running. The server enforces the same rules.
 */
export function timeoutActionFor(input: {
  viewerUserId: string;
  canModerate: boolean;
  targetUserId: string;
  targetRole: "commissioner" | "admin" | "member";
  targetTimedOutUntilIso: string | null | undefined;
  now: Date;
}): "timeout" | "endTimeout" | null {
  if (!input.canModerate) return null;
  if (input.targetUserId === input.viewerUserId) return null;
  if (input.targetRole !== "member") return null;
  return activeTimeoutIso(input.targetTimedOutUntilIso, input.now) === null
    ? "timeout"
    : "endTimeout";
}
